import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Send, Smile, AtSign } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useUsers } from '@/hooks/useUsers';
import type { User } from '@/api/users';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { MentionInput } from '@/components/ui/mention-input';
import { useMentionUsers } from '@/hooks/useMentionUsers';

interface CommentInputProps {
    value?: string;
    onSubmit: (value: string) => Promise<void>;
    onCancel?: () => void;
    placeholder?: string;
    submitLabel?: string;
    isSubmitting?: boolean;
    autoFocus?: boolean;
}

export function CommentInput({
    value = '',
    onSubmit,
    onCancel,
    placeholder = 'Leave a comment...',
    submitLabel = 'Post Comment',
    isSubmitting = false,
    autoFocus = false,
}: CommentInputProps) {
    const [content, setContent] = useState(value);
    const [mentionQuery, setMentionQuery] = useState<string | null>(null);
    const [mentionPosition, setMentionPosition] = useState<{ top: number; left: number } | null>(null);
    const { data: usersData } = useUsers({ limit: 10, search: mentionQuery || '' });
    const users = usersData?.data || [];

    // We need to fetch users for existing mentions to display them correctly
    // Wrap content in object array for hook
    const { data: userMap } = useMentionUsers([{ comment: content }]);

    const handleSubmit = async () => {
        if (!content.trim()) return;
        await onSubmit(content);
        setContent('');
    };

    const insertMention = (user: User) => {
        if (!mentionQuery) return;

        // We need to replace the query with the mention
        // Since we don't have exact cursor pos here easily (it's in the child),
        // we rely on the fact that mentionQuery is the last thing typed.
        // This is a simplification. A robust solution would pass cursor pos from child.

        // Actually, let's just replace the last occurrence of @query
        const lastIndex = content.lastIndexOf(`@${mentionQuery}`);
        if (lastIndex !== -1) {
            const before = content.slice(0, lastIndex);
            const after = content.slice(lastIndex + mentionQuery.length + 1);
            const newContent = `${before}@${user.id} ${after}`;
            setContent(newContent);
        }

        setMentionQuery(null);
        setMentionPosition(null);
    };

    const insertEmoji = (emoji: any) => {
        setContent(prev => prev + emoji.native);
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="relative">
                <MentionInput
                    value={content}
                    onChange={setContent}
                    onTriggerMention={(query, position) => {
                        setMentionQuery(query);
                        setMentionPosition(position);
                    }}
                    onCloseMention={() => {
                        setMentionQuery(null);
                        setMentionPosition(null);
                    }}
                    userMap={userMap}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    disabled={isSubmitting}
                />

                {mentionQuery !== null && mentionPosition && users && users.length > 0 && (
                    <div
                        className="fixed z-50 w-64 p-1 bg-popover text-popover-foreground rounded-md border shadow-md"
                        style={{
                            top: mentionPosition.top + 10,
                            left: mentionPosition.left
                        }}
                    >
                        <Command>
                            <CommandList>
                                <CommandGroup>
                                    {users.map((user) => (
                                        <CommandItem
                                            key={user.id}
                                            onSelect={() => insertMention(user)}
                                            className="flex items-center gap-2 cursor-pointer"
                                        >
                                            <Avatar className="h-6 w-6">
                                                <AvatarImage src={user.avatar ? `/assets/${user.avatar}` : undefined} />
                                                <AvatarFallback>{user.first_name?.[0]}{user.last_name?.[0]}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">
                                                    {user.first_name} {user.last_name}
                                                </span>
                                                <span className="text-xs text-muted-foreground">
                                                    {user.email}
                                                </span>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                                <Smile className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Picker data={data} onEmojiSelect={insertEmoji} theme="light" />
                        </PopoverContent>
                    </Popover>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setContent(prev => prev + '@')}
                    >
                        <AtSign className="h-4 w-4" />
                    </Button>
                </div>
                <div className="flex items-center gap-2">
                    {onCancel && (
                        <Button variant="ghost" size="sm" onClick={onCancel}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        size="sm"
                        onClick={handleSubmit}
                        disabled={!content.trim() || isSubmitting}
                    >
                        {isSubmitting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="mr-2 h-4 w-4" />
                        )}
                        {submitLabel}
                    </Button>
                </div>
            </div>
        </div>
    );
}
